import React, { useState, useEffect, useRef } from 'react';
import { Edit3, Save, X, Clock, AlertCircle, Check } from 'lucide-react';
import { ContentEditorProps } from '../../types/components';

const ContentEditor: React.FC<ContentEditorProps> = ({
  value,
  onChange,
  placeholder = '请输入内容...',
  isEditing,
  onEditToggle,
  onSave,
  onCancel,
  autoSave = true,
  onAutoSave,
  className = '',
  label,
  maxLength = 5000
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [validationError, setValidationError] = useState<string | null>(null);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 同步外部值变化
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value);
      setHasUnsavedChanges(false);
    }
  }, [value, isEditing]);

  // 自动保存逻辑
  useEffect(() => {
    if (!autoSave || !onAutoSave || !isEditing || !hasUnsavedChanges) {
      return;
    }

    // 清除之前的定时器
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // 设置新的自动保存定时器
    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        setAutoSaveStatus('saving');
        await onAutoSave(localValue);
        setAutoSaveStatus('saved');
        setLastSavedAt(new Date());
        setHasUnsavedChanges(false);
        
        // 2秒后重置状态
        setTimeout(() => {
          setAutoSaveStatus('idle');
        }, 2000);
      } catch (error) {
        setAutoSaveStatus('error');
        console.error('自动保存失败:', error);
      }
    }, 3000); // 3秒延迟

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [localValue, hasUnsavedChanges, autoSave, onAutoSave, isEditing]);

  // 编辑模式切换时聚焦
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      // 将光标移到末尾
      const length = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  const handleValueChange = (newValue: string) => {
    // 长度验证
    if (newValue.length > maxLength) {
      setValidationError(`内容长度不能超过 ${maxLength} 个字符`);
      return;
    } else {
      setValidationError(null);
    }

    setLocalValue(newValue);
    onChange(newValue);
    setHasUnsavedChanges(newValue !== value);
  };

  const handleSave = async () => {
    if (validationError) {
      return;
    }

    try {
      await onSave();
      setHasUnsavedChanges(false);
      setLastSavedAt(new Date());
    } catch (error) {
      console.error('保存失败:', error);
    }
  };

  const handleCancel = () => {
    setLocalValue(value);
    setHasUnsavedChanges(false);
    setValidationError(null);
    onCancel();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl/Cmd + S 保存
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
    
    // Escape 取消
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const getAutoSaveStatusText = () => {
    switch (autoSaveStatus) {
      case 'saving':
        return '正在保存...';
      case 'saved':
        return '已自动保存';
      case 'error':
        return '自动保存失败';
      default:
        return '';
    }
  };

  const getAutoSaveStatusIcon = () => {
    switch (autoSaveStatus) {
      case 'saving':
        return <Clock className="w-3 h-3 animate-spin" />;
      case 'saved':
        return <Check className="w-3 h-3 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-3 h-3 text-red-500" />;
      default:
        return null;
    }
  };

  if (!isEditing) {
    // 查看模式
    return (
      <div className={`group ${className}`}>
        {label && (
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">{label}</label>
            <button
              onClick={onEditToggle}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-gray-600"
              title="编辑"
            >
              <Edit3 className="w-4 h-4" />
            </button>
          </div>
        )}
        
        <div 
          className="min-h-[100px] p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={onEditToggle}
        >
          {value ? (
            <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">
              {value}
            </div>
          ) : (
            <div className="text-gray-500 italic">
              {placeholder}
            </div>
          )}
        </div>
        
        {lastSavedAt && (
          <div className="mt-2 text-xs text-gray-500">
            最后保存：{lastSavedAt.toLocaleString('zh-CN')}
          </div>
        )}
      </div>
    );
  }

  // 编辑模式
  return (
    <div className={className}>
      {label && (
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">{label}</label>
          <div className="flex items-center space-x-2">
            {/* 自动保存状态 */}
            {autoSave && (
              <div className="flex items-center space-x-1 text-xs text-gray-500">
                {getAutoSaveStatusIcon()}
                <span>{getAutoSaveStatusText()}</span>
              </div>
            )}
            
            {/* 字符计数 */}
            <span className={`text-xs ${
              localValue.length > maxLength * 0.9 
                ? 'text-red-500' 
                : 'text-gray-500'
            }`}>
              {localValue.length}/{maxLength}
            </span>
          </div>
        </div>
      )}

      <div className="relative">
        <textarea
          ref={textareaRef}
          value={localValue}
          onChange={(e) => handleValueChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`
            w-full min-h-[200px] p-4 border rounded-lg resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors
            ${validationError 
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
              : 'border-gray-300'
            }
          `}
          style={{ fontFamily: 'inherit' }}
        />
        
        {/* 验证错误 */}
        {validationError && (
          <div className="absolute -bottom-6 left-0 flex items-center space-x-1 text-xs text-red-500">
            <AlertCircle className="w-3 h-3" />
            <span>{validationError}</span>
          </div>
        )}
      </div>

      {/* 操作按钮 */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center space-x-2">
          {hasUnsavedChanges && !autoSave && (
            <span className="text-xs text-orange-600 flex items-center space-x-1">
              <AlertCircle className="w-3 h-3" />
              <span>有未保存的更改</span>
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={handleCancel}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            <X className="w-4 h-4 inline mr-1" />
            取消
          </button>
          
          <button
            onClick={handleSave}
            disabled={!!validationError}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
          >
            <Save className="w-4 h-4" />
            <span>保存</span>
          </button>
        </div>
      </div>

      {/* 快捷键提示 */}
      <div className="mt-2 text-xs text-gray-500">
        快捷键：Ctrl+S 保存，Esc 取消
      </div>
    </div>
  );
};

export default ContentEditor;